import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { BarChart3, ExternalLink, LayoutDashboard, LogOut, Package, Settings, Tags } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/categories", label: "Categories", icon: Tags },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

function Logo() {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-black font-heading text-sm font-bold text-white">Ti</div>
      <div>
        <div className="font-heading text-sm font-semibold tracking-tight">TECH iELA</div>
        <div className="text-[11px] text-zinc-400">Admin Studio</div>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#FBFBFB]" data-testid="admin-dashboard">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-black/5 bg-white lg:flex">
        <div className="border-b border-black/5 px-6 py-6">
          <Logo />
        </div>
        <nav className="flex-1 space-y-1 px-3 py-4">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`admin-nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm transition-colors duration-200 ${
                  isActive ? "bg-black text-white" : "text-zinc-500 hover:bg-zinc-100 hover:text-black"
                }`
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="space-y-1 border-t border-black/5 px-3 py-4">
          <Link
            to="/"
            target="_blank"
            data-testid="admin-view-site-link"
            className="flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-black"
          >
            <ExternalLink className="h-4 w-4" />
            View site
          </Link>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-button"
            className="flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-black"
          >
            <LogOut className="h-4 w-4" />
            Log out
          </button>
        </div>
      </aside>

      <div className="lg:pl-64">
        <div className="sticky top-0 z-30 border-b border-black/5 bg-white/85 backdrop-blur-xl lg:hidden">
          <div className="flex items-center justify-between px-5 py-3">
            <Logo />
            <button
              onClick={handleLogout}
              aria-label="Log out"
              data-testid="admin-logout-button-mobile"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-black/10 text-zinc-500"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
          <nav className="scrollbar-none flex gap-2 overflow-x-auto px-5 pb-3">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`admin-nav-mobile-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `shrink-0 rounded-full px-4 py-1.5 text-sm transition-colors ${
                    isActive ? "bg-black text-white" : "border border-black/10 bg-white text-zinc-500"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
        <main className="p-5 sm:p-8 lg:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
